import React, { useMemo, useState } from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { getBranch, Service, formatUsd } from '../data/catalog';
import { tutorsForBranch } from '../data/tutors';
import { createCheckout } from '../services/api';
import ServiceCard from '../components/ServiceCard';
import TutorCard from '../components/TutorCard';
import usePageMeta from '../hooks/usePageMeta';
import '../styles/Branch.css';

const SITE_URL = 'https://bulgebracket.co';

const BranchPage: React.FC = () => {
  const { branchId } = useParams();
  const [searchParams] = useSearchParams();
  const branch = getBranch(branchId);
  const [buyingId, setBuyingId] = useState<string | null>(null);
  const [checkoutError, setCheckoutError] = useState(false);
  const cancelled = searchParams.get('cancelled') === '1';

  const services = useMemo(() => (branch ? branch.services : []), [branch]);

  const jsonLd = useMemo(() => {
    if (!branch) return [];
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: `${branch.name} interview preparation`,
        provider: { '@type': 'Organization', name: 'Bulge Bracket', url: SITE_URL },
        areaServed: 'GB',
        description: branch.seoDescription,
        hasOfferCatalog: {
          '@type': 'OfferCatalog',
          name: `${branch.name} prep services`,
          itemListElement: services.map(service => ({
            '@type': 'Offer',
            name: service.name,
            price: service.priceUsd,
            priceCurrency: 'USD',
            url: `${SITE_URL}/${branch.id}`
          }))
        }
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: branch.faqs.map(faq => ({
          '@type': 'Question',
          name: faq.q,
          acceptedAnswer: { '@type': 'Answer', text: faq.a }
        }))
      }
    ];
  }, [branch, services]);

  usePageMeta({
    title: branch ? branch.seoTitle : 'Bulge Bracket',
    description: branch ? branch.seoDescription : '',
    path: branch ? `/${branch.id}` : '/',
    jsonLd
  });

  if (!branch) {
    return <Navigate to="/" replace />;
  }

  const singles = services.filter(service => service.kind === 'single');
  const combos = services.filter(service => service.kind === 'block' || service.kind === 'combo');
  const fullPackage = services.find(service => service.kind === 'package');
  const unlimited = services.find(service => service.kind === 'unlimited');
  const tutors = tutorsForBranch(branch.id);

  const handleBuy = async (service: Service): Promise<void> => {
    setCheckoutError(false);
    setBuyingId(service.id);
    try {
      const url = await createCheckout(branch.id, service.id);
      window.location.assign(url);
    } catch (error) {
      setCheckoutError(true);
      setBuyingId(null);
    }
  };

  return (
    <>
      <section className="branch-hero">
        <div className="container">
          {cancelled && (
            <p className="checkout-notice" role="status">
              Checkout cancelled. Nothing was charged.
            </p>
          )}
          <p className="section-eyebrow">Track · {branch.shortName}</p>
          <h1 className="branch-title">{branch.heroTitle}</h1>
          <p className="branch-lede">{branch.heroLede}</p>
          <ul className="check-list branch-drills">
            {branch.drills.map(drill => (
              <li key={drill}>{drill}</li>
            ))}
          </ul>
        </div>
      </section>

      <section id="services" className="section branch-services" aria-label={`${branch.name} services`}>
        <div className="container">
          <div className="section-head" data-reveal>
            <p className="section-eyebrow">The services</p>
            <h2 className="section-title">Buy exactly the prep you need.</h2>
            <p className="section-intro">
              Everything happens on a call with your tutor. Pay, then book.
              Unused sessions are refundable for 30 days.
            </p>
          </div>

          {checkoutError && (
            <p className="checkout-notice checkout-notice-error" role="alert">
              Couldn&apos;t open checkout. Try again, or email hello@bulgebracket.co.
            </p>
          )}

          <div className="service-grid">
            {singles.map((service, index) => (
              <ServiceCard
                key={service.id}
                service={service}
                onBuy={handleBuy}
                buyingId={buyingId}
                revealDelay={index * 0.08}
              />
            ))}
          </div>

          <div className="bundle-grid">
            {combos.map((service, index) => (
              <ServiceCard
                key={service.id}
                service={service}
                onBuy={handleBuy}
                buyingId={buyingId}
                revealDelay={index * 0.08}
              />
            ))}
            {fullPackage && (
              <ServiceCard service={fullPackage} onBuy={handleBuy} buyingId={buyingId} />
            )}
          </div>

          {unlimited && (
            <div className="unlimited-panel" data-reveal>
              <div className="unlimited-copy">
                <p className="unlimited-kicker">Unlimited</p>
                <h3 className="unlimited-title">{unlimited.name}</h3>
                <p className="unlimited-blurb">{unlimited.blurb}</p>
                <ul className="check-list unlimited-features">
                  {unlimited.features.map(feature => (
                    <li key={feature}>{feature}</li>
                  ))}
                </ul>
              </div>
              <div className="unlimited-buy">
                <p className="price unlimited-price">{formatUsd(unlimited.priceUsd)}</p>
                <p className="price-note">One recruiting cycle</p>
                <button
                  className="btn btn-on-panel unlimited-cta"
                  onClick={() => handleBuy(unlimited)}
                  disabled={buyingId !== null}
                >
                  {buyingId === unlimited.id ? 'Opening checkout…' : 'Go unlimited'}
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      <section id="tutors" className="section section-alt branch-tutors">
        <div className="container">
          <div className="section-head" data-reveal>
            <p className="section-eyebrow">Your tutors</p>
            <h2 className="section-title">Check availability before you buy.</h2>
            <p className="section-intro">
              All {branch.name} tutors sit in the seats you&apos;re
              interviewing for, today. Open a calendar, find a time you like,
              then buy your prep. Nothing is confirmed until after checkout,
              and you&apos;ll come straight back here to lock the slot in.
            </p>
          </div>
          <div className="tutor-grid">
            {tutors.map((tutor, index) => (
              <TutorCard key={tutor.id} tutor={tutor} revealDelay={index * 0.08} />
            ))}
          </div>
        </div>
      </section>

      <section className="section branch-faq">
        <div className="container">
          <div className="section-head" data-reveal>
            <p className="section-eyebrow">{branch.shortName} questions</p>
            <h2 className="section-title">Before you buy.</h2>
          </div>
          <div className="faq-list" data-reveal>
            {branch.faqs.map(faq => (
              <details key={faq.q} className="faq-item">
                <summary>{faq.q}</summary>
                <p>{faq.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default BranchPage;
